import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { timezoneOptions } from "../conts/timezoneOptions";
import { TimezoneOption } from "../types/TimezoneOption";
import { getBrowserTimezone } from "../utils/getBrowserTimezone";
import { getTimezoneOptionLabel } from "../utils/getTimezoneOptionLabel";

/**
 * The fixed timezone options, translated, preceded by the browser's zone and
 * the selected zone when the fixed list lacks them.
 */
export const useLocalizedTimezoneOptions = (
  selectedTimezone?: string,
): TimezoneOption[] => {
  const { t } = useTranslation();
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);

  return useMemo(() => {
    const fixedOptions = timezoneOptions.map((option) => ({
      ...option,
      label: t(option.label),
    }));
    const extraTimezones = [browserTimezone, selectedTimezone].filter(
      (timezone, index, timezones): timezone is string =>
        !!timezone &&
        timezones.indexOf(timezone) === index &&
        !fixedOptions.some((option) => option.value === timezone),
    );
    const extraOptions = extraTimezones.map((timezone) => ({
      label: getTimezoneOptionLabel(timezone),
      value: timezone,
    }));

    return [...extraOptions, ...fixedOptions];
  }, [t, browserTimezone, selectedTimezone]);
};
